import { reactive, watch } from 'vue';
import { getPreferences } from '@/client/utils/PreferencesManager';
import { safeLocalStorage } from '@/client/utils/SafeLocalStorage';
import { MUSIC_TRACKS, SFX_DURATION, playSfx, renderTrack } from '@/client/utils/SoundSynth';
/** Volumes (0-100) and the music track choice. Persisted in local storage; the on/off switches are preferences. */
export const soundSettings = reactive({
    musicVolume: 50,
    effectsVolume: 70,
    /** 'auto' rotates through the tracks, otherwise a track id. */
    track: 'auto',
});
const KEYS = { musicVolume: 'sound_music_volume', effectsVolume: 'sound_effects_volume', track: 'sound_music_track' };
function clampVolume(v, fallback) {
    return Number.isFinite(v) ? Math.max(0, Math.min(100, Math.round(v))) : fallback;
}
export function loadSoundSettings() {
    const m = safeLocalStorage.getItem(KEYS.musicVolume);
    const e = safeLocalStorage.getItem(KEYS.effectsVolume);
    const t = safeLocalStorage.getItem(KEYS.track);
    if (m !== null) {
        soundSettings.musicVolume = clampVolume(Number(m), 50);
    }
    if (e !== null) {
        soundSettings.effectsVolume = clampVolume(Number(e), 70);
    }
    if (t !== null) {
        soundSettings.track = t;
    }
}
export function saveSoundSettings() {
    soundSettings.musicVolume = clampVolume(Number(soundSettings.musicVolume), 50);
    soundSettings.effectsVolume = clampVolume(Number(soundSettings.effectsVolume), 70);
    safeLocalStorage.setItem(KEYS.musicVolume, String(soundSettings.musicVolume));
    safeLocalStorage.setItem(KEYS.effectsVolume, String(soundSettings.effectsVolume));
    safeLocalStorage.setItem(KEYS.track, soundSettings.track);
}
/** The same effect is ignored if it repeats within this many milliseconds (bots act quickly). */
export const DEDUPE_MS = 80;
/** At most this many effects may sound at once; later ones are dropped (except high priority ones). */
export const MAX_VOICES = 6;
const PRIORITY = new Set(['turn', 'victory', 'error']);
/** Seconds of crossfade between music tracks. */
const FADE = 2.5;
/** In auto mode every track plays this many times before the next one. */
const LOOPS_PER_TRACK = 2;
const volumeToGain = (v, max) => (v / 100) * (v / 100) * max;
/**
 * Sound engine. Two separate AudioContexts: effects can never disturb the music.
 * Everything is lazy (nothing is created before the first user gesture) and fails soft without WebAudio.
 */
export class SoundEngine {
    constructor(deps) {
        this.deps = deps;
        this.unlocked = false;
        this.lastPlayed = new Map();
        this.voices = [];
        this.rendered = new Map();
        this.trackIndex = -1;
        this.trackSetting = '';
        this.musicRunning = false;
        this.token = 0;
        /** Counters for diagnostics and tests. */
        this.stats = { played: 0, dropped: 0, tracksStarted: 0 };
    }
    /** Plays one effect, if effects are enabled. Safe to call at any time. */
    play(name) {
        try {
            const cfg = this.deps.config();
            if (!cfg.effects || cfg.effectsVolume <= 0) {
                return;
            }
            const now = this.deps.now();
            const last = this.lastPlayed.get(name);
            if (last !== undefined && now - last < DEDUPE_MS) {
                this.stats.dropped++;
                return;
            }
            this.voices = this.voices.filter((end) => end > now);
            if (this.voices.length >= MAX_VOICES && !PRIORITY.has(name)) {
                this.stats.dropped++;
                return;
            }
            const ctx = this.ensureSfx();
            if (ctx === undefined || this.sfxGain === undefined) {
                return;
            }
            this.lastPlayed.set(name, now);
            this.voices.push(now + SFX_DURATION[name] * 1000);
            if (ctx.state !== 'running') {
                ctx.resume().catch(() => { });
            }
            this.sfxGain.gain.value = volumeToGain(cfg.effectsVolume, 2.6);
            playSfx(ctx, this.sfxGain, name, ctx.currentTime + 0.02, name === 'turn' && this.deps.isHidden());
            this.stats.played++;
        }
        catch (e) {
            console.warn('sound effect failed', e);
        }
    }
    /** Call from a user gesture: allows audio and starts the music if it is enabled. */
    unlock() {
        this.unlocked = true;
        try {
            this.ensureSfx();
            if (this.sfxCtx !== undefined && this.sfxCtx.state !== 'running') {
                this.sfxCtx.resume().catch(() => { });
            }
        }
        catch (e) {
            console.warn('unable to unlock audio', e);
        }
        this.applySettings();
    }
    /** Re-reads the settings: starts, stops, retunes or re-selects the music. */
    applySettings() {
        try {
            const cfg = this.deps.config();
            if (this.sfxGain !== undefined) {
                this.sfxGain.gain.value = volumeToGain(cfg.effectsVolume, 2.6);
            }
            const want = this.unlocked && cfg.music && cfg.musicVolume > 0;
            if (!want) {
                if (this.musicRunning) {
                    this.stopMusic();
                }
                return;
            }
            if (!this.musicRunning) {
                this.startMusic(cfg);
                return;
            }
            if (this.musicGain !== undefined && this.musicCtx !== undefined) {
                this.musicGain.gain.setTargetAtTime(volumeToGain(cfg.musicVolume, 0.4), this.musicCtx.currentTime, 0.1);
            }
            if (cfg.track !== this.trackSetting) {
                this.trackSetting = cfg.track;
                this.playTrack(this.chooseTrack(cfg.track), cfg);
            }
        }
        catch (e) {
            console.warn('unable to apply sound settings', e);
        }
    }
    /** Pauses the music while the tab is hidden and resumes it afterwards. Effects keep working in the background. */
    visibilityChanged() {
        const ctx = this.musicCtx;
        if (ctx === undefined || !this.musicRunning) {
            return;
        }
        if (this.deps.isHidden()) {
            ctx.suspend().catch(() => { });
        }
        else {
            ctx.resume().catch(() => { });
        }
    }
    isMusicRunning() {
        return this.musicRunning;
    }
    currentTrackId() {
        return MUSIC_TRACKS[this.trackIndex]?.id;
    }
    dispose() {
        this.stopMusic();
        for (const c of [this.sfxCtx, this.musicCtx]) {
            c?.close().catch(() => { });
        }
        this.sfxCtx = this.sfxGain = this.musicCtx = this.musicGain = undefined;
        this.rendered.clear();
        this.unlocked = false;
    }
    ensureSfx() {
        if (this.sfxCtx === undefined) {
            const ctx = this.deps.createContext();
            if (ctx === undefined) {
                return undefined;
            }
            const comp = ctx.createDynamicsCompressor();
            comp.threshold.value = -14;
            comp.knee.value = 12;
            comp.ratio.value = 8;
            comp.attack.value = 0.003;
            comp.release.value = 0.2;
            const gain = ctx.createGain();
            gain.gain.value = 0;
            gain.connect(comp);
            comp.connect(ctx.destination);
            this.sfxCtx = ctx;
            this.sfxGain = gain;
        }
        return this.sfxCtx;
    }
    ensureMusic() {
        if (this.musicCtx === undefined) {
            const ctx = this.deps.createContext();
            if (ctx === undefined) {
                return undefined;
            }
            const limiter = ctx.createDynamicsCompressor();
            limiter.threshold.value = -6;
            limiter.ratio.value = 12;
            const gain = ctx.createGain();
            gain.gain.value = 0;
            gain.connect(limiter);
            limiter.connect(ctx.destination);
            this.musicCtx = ctx;
            this.musicGain = gain;
        }
        return this.musicCtx;
    }
    chooseTrack(setting) {
        const fixed = MUSIC_TRACKS.findIndex((t) => t.id === setting);
        if (fixed >= 0) {
            return fixed;
        }
        if (this.trackIndex < 0) {
            // Start somewhere different each time.
            return Math.floor((this.deps.now() / 1000) % MUSIC_TRACKS.length);
        }
        return (this.trackIndex + 1) % MUSIC_TRACKS.length;
    }
    startMusic(cfg) {
        const ctx = this.ensureMusic();
        if (ctx === undefined || this.musicGain === undefined) {
            return;
        }
        if (this.deps.createOffline(1, 8000) === undefined) {
            return;
        } // No offline rendering: no music.
        this.musicRunning = true;
        this.trackSetting = cfg.track;
        if (ctx.state !== 'running' && !this.deps.isHidden()) {
            ctx.resume().catch(() => { });
        }
        this.musicGain.gain.setValueAtTime(volumeToGain(cfg.musicVolume, 0.4), ctx.currentTime);
        this.playTrack(this.chooseTrack(cfg.track), cfg);
    }
    stopMusic() {
        this.musicRunning = false;
        this.token++;
        if (this.timer !== undefined) {
            this.deps.clearTimer(this.timer);
        }
        this.timer = undefined;
        const cur = this.current;
        this.current = undefined;
        this.trackIndex = -1;
        if (cur !== undefined && this.musicCtx !== undefined) {
            const t = this.musicCtx.currentTime;
            cur.gain.gain.cancelScheduledValues(t);
            cur.gain.gain.setTargetAtTime(0, t, 0.1);
            try {
                cur.source.stop(t + 0.6);
            }
            catch (e) { /* already stopped */ }
        }
    }
    render(track) {
        let p = this.rendered.get(track.id);
        if (p === undefined) {
            p = renderTrack(track, (len, sr) => {
                const oc = this.deps.createOffline(len, sr);
                if (oc === undefined) {
                    throw new Error('no OfflineAudioContext');
                }
                return oc;
            }).then((r) => {
                const ctx = this.musicCtx;
                if (ctx === undefined) {
                    return undefined;
                }
                const buf = ctx.createBuffer(1, r.data.length, r.sampleRate);
                buf.copyToChannel(r.data, 0);
                return buf;
            }).catch((e) => {
                console.warn('unable to render music', e);
                this.rendered.delete(track.id);
                return undefined;
            });
            this.rendered.set(track.id, p);
        }
        return p;
    }
    playTrack(index, cfg) {
        const track = MUSIC_TRACKS[index];
        const token = ++this.token;
        if (this.timer !== undefined) {
            this.deps.clearTimer(this.timer);
        }
        this.timer = undefined;
        this.render(track).then((buf) => {
            const ctx = this.musicCtx;
            if (token !== this.token || buf === undefined || ctx === undefined || this.musicGain === undefined) {
                return;
            }
            const t = ctx.currentTime;
            const prev = this.current;
            if (prev !== undefined) {
                prev.gain.gain.cancelScheduledValues(t);
                prev.gain.gain.setValueAtTime(prev.gain.gain.value, t);
                prev.gain.gain.linearRampToValueAtTime(0, t + FADE);
                try {
                    prev.source.stop(t + FADE + 0.1);
                }
                catch (e) { /* already stopped */ }
            }
            const source = ctx.createBufferSource();
            const gain = ctx.createGain();
            source.buffer = buf;
            source.loop = true;
            gain.gain.setValueAtTime(0, t);
            gain.gain.linearRampToValueAtTime(1, t + (prev === undefined ? 1.5 : FADE));
            source.connect(gain);
            gain.connect(this.musicGain);
            source.start(t);
            this.current = { source, gain };
            this.trackIndex = index;
            this.stats.tracksStarted++;
            if (cfg.track === 'auto') {
                this.timer = this.deps.setTimer(() => {
                    this.timer = undefined;
                    if (token === this.token && this.musicRunning) {
                        this.playTrack(this.chooseTrack('auto'), this.deps.config());
                    }
                }, track.length * LOOPS_PER_TRACK * 1000);
            }
        });
    }
}
function realContext() {
    try {
        const w = window;
        const AC = w.AudioContext ?? w.webkitAudioContext;
        return AC === undefined ? undefined : new AC();
    }
    catch (e) {
        return undefined;
    }
}
function realOffline(length, sampleRate) {
    try {
        const w = window;
        const OAC = w.OfflineAudioContext ?? w.webkitOfflineAudioContext;
        return OAC === undefined ? undefined : new OAC(1, length, sampleRate);
    }
    catch (e) {
        return undefined;
    }
}
export function currentSoundConfig() {
    const prefs = getPreferences();
    return {
        effects: prefs.enable_sounds,
        music: prefs.enable_music,
        effectsVolume: soundSettings.effectsVolume,
        musicVolume: soundSettings.musicVolume,
        track: soundSettings.track,
    };
}
export const SoundManager = Object.assign(new SoundEngine({
    createContext: realContext,
    createOffline: realOffline,
    now: () => Date.now(),
    isHidden: () => typeof document !== 'undefined' && document.hidden === true,
    config: currentSoundConfig,
    setTimer: (fn, ms) => setTimeout(fn, ms),
    clearTimer: (h) => clearTimeout(h),
}), {
    /** Kept for existing callers: the "your turn" chime. */
    playActivePlayerSound() {
        SoundManager.play('turn');
    },
    newLog() {
        SoundManager.play('log');
    },
});
let hooksInstalled = false;
/** Wires the singleton to the page: first-gesture unlock, button click sounds, tab visibility, setting changes. */
export function installSoundHooks() {
    if (hooksInstalled || typeof document === 'undefined') {
        return;
    }
    hooksInstalled = true;
    loadSoundSettings();
    const unlock = () => SoundManager.unlock();
    document.addEventListener('pointerdown', unlock, true);
    document.addEventListener('keydown', unlock, true);
    document.addEventListener('visibilitychange', () => SoundManager.visibilityChanged());
    document.addEventListener('click', (e) => {
        const el = e.target instanceof Element ? e.target.closest('button, .btn') : null;
        if (el === null) {
            return;
        }
        if (el.closest('[data-no-sound]') !== null) {
            return;
        }
        if (el.matches('.btn-error, [data-test*="cancel"], [data-test*="close"]')) {
            SoundManager.play('cancel');
        }
        else if (el.matches('.btn-primary, .btn-success, .btn-submit, [data-test*="confirm"], [data-test*="submit"]')) {
            SoundManager.play('confirm');
        }
        else {
            SoundManager.play('click');
        }
    }, true);
    watch(() => currentSoundConfig(), () => SoundManager.applySettings(), { deep: true });
}
